import { InvolvementScale } from '@/components/admin/reglages/involvement-scale';
import { ToneLibrary } from '@/components/admin/reglages/tone-library';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import {
  deleteInvolvementLevel,
  deletePersonaTone,
  fetchInvolvementLevels,
  fetchPersonaTones,
  saveInvolvementLevels,
  savePersonaTones,
} from '@/lib/api';
import { personaReferenceMessage, writeSettledOnTheBase } from '@/lib/persona-labels';
import type { ScaleRow, ToneRow } from '@/lib/persona-settings';
import { scaleBlockOf, toneBlockOf } from '@/lib/persona-settings';
import { Spinner } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * Réglages > Personas (spec §11.5, planche `ReglagesPersonas`).
 *
 * The TWO reference lists a persona's versions copy from: the involvement scale
 * its slider walks, and the tone library its editor offers as a cloud of tags.
 * They are here because they belong to nobody in particular - a persona keeps
 * only a copy of what it took - and NEITHER IS VERSIONED, which is the one
 * thing the screen has to say out loud rather than leave to the spec.
 *
 * The page itself is not here: `/admin/personas` is still a couture, and issue
 * #42 fills it. This tab exists first because the editor of that lot cannot be
 * tested without a scale to slide over.
 *
 * ## One request per section, and one transaction behind it (issue #45)
 *
 * The planche fixes « chaque section s'enregistre d'un bloc, avec son propre
 * bouton », and since #45 the API says the same thing: `PUT` on the collection
 * carries the whole list, and the server applies rewrites, creations and order
 * together or not at all.
 *
 * It used to be a REPLAY of per-row writes over CRUD routes - the rewrites,
 * then the creations that handed the new rows their identifiers, then the
 * order - and it cost two defects: exchanging the names of two crans was
 * refused on the first write, on a name the operator was in the middle of
 * moving, and a refusal halfway through left the base half written. The block
 * that goes out is still built by a pure function in
 * `lib/persona-settings.ts`, because `vitest.config.ts` leaves `src/routes/**`
 * out of the coverage report and a rule nothing measures is a rule that rots.
 *
 * A DELETION does not wait for the button: it carries its own confirmation, it
 * counts what it touches, and the base re-densifies the positions behind it -
 * three things that would be a lie inside a pending block. It is the one
 * gesture of this screen that is still its own route.
 *
 * ## The two `key`s
 *
 * Each section owns its draft and is initialised from its props, never by an
 * effect. « Recharger » is therefore a REMOUNT, and the counters below are what
 * force it once a write has landed - the same régime as
 * `criteres.$criterionId`. A counter and not the data itself: a text-only save
 * changes no identifier, so a key built from the rows would not move.
 *
 * ## A failed TRANSPORT does not throw the draft away
 *
 * Which is why the two halves of what follows a write - re-read the base,
 * remount the section on it - are separate, and only the first is
 * unconditional. `writeSettledOnTheBase` says which outcomes get the second
 * one: a success and a typed refusal of §11.5, both of them states of the base.
 * A dropped connection, a 500 or a 400 settle nothing, so the rows the operator
 * typed stay on screen with the failure under them, and the button can be
 * pressed again. Losing four thousand characters of direction to a flaky
 * network would be a punishment for a failure that was not theirs.
 */

export const Route = createFileRoute('/admin/reglages/personas')({
  component: ReglagesPersonasPage,
});

/** Which of the two lists a write belongs to: they settle one at a time. */
type Section = 'scale' | 'tones';

/**
 * EXPORTED for `tests/reglages-personas.test.tsx`, and for nothing else.
 *
 * `vitest.config.ts` leaves `src/routes/**` out of the coverage REPORT, not out
 * of the suite. What this component decides after a write is a rule of its own
 * - which failures cost the operator their draft - and it cannot be seen from
 * the two sections, which never learn why they were remounted.
 */
export function ReglagesPersonasPage() {
  const { email } = useAdminSession();
  const queryClient = useQueryClient();
  const [scaleGeneration, setScaleGeneration] = useState(0);
  const [toneGeneration, setToneGeneration] = useState(0);
  const [deletingLevel, setDeletingLevel] = useState<string | null>(null);
  const [deletingTone, setDeletingTone] = useState<string | null>(null);

  const levels = useQuery({
    queryKey: ['involvement-levels', email],
    queryFn: () => fetchInvolvementLevels(),
    retry: false,
  });
  const tones = useQuery({
    queryKey: ['persona-tones', email],
    queryFn: () => fetchPersonaTones(),
    retry: false,
  });

  /** Re-reads the section from the base. Done after EVERY write, whatever it did. */
  function reread(section: Section): Promise<void> {
    return queryClient.invalidateQueries({
      queryKey: [section === 'scale' ? 'involvement-levels' : 'persona-tones'],
    });
  }

  /** Remounts the section on what came back, which DROPS the rows in the page. */
  function remount(section: Section): void {
    if (section === 'scale') setScaleGeneration((count) => count + 1);
    else setToneGeneration((count) => count + 1);
  }

  /**
   * What follows a write, in its two halves - and only the first is
   * unconditional.
   *
   * The re-read always: the base may have moved, and this screen is the one
   * place that shows it. The remount only when the write SETTLED on a state of
   * the base - it landed, or the server refused it with a code of §11.5. A
   * refusal is a state of the base, so coming back to the base is the honest
   * screen; re-reading on success only would leave the operator editing a stale
   * section and pressing a button that cannot succeed, and the price - the
   * edits of that block - is paid deliberately.
   *
   * A dropped connection, a 500 or a 400 settle NOTHING, and there the price is
   * refused: the rows stay, the failure shows under the section, and the button
   * can be pressed again.
   */
  async function settle(section: Section, error: unknown): Promise<void> {
    await reread(section);
    if (writeSettledOnTheBase(error)) remount(section);
  }

  const saveScale = useMutation({
    mutationFn: (rows: readonly ScaleRow[]) => saveInvolvementLevels(scaleBlockOf(rows)),
    // `onSettled` and NOT `onSuccess`, and it stays that way after issue #45:
    // what it protected against is gone (a block is one transaction, so it
    // cannot land halfway), but a REFUSAL still has to come back to the base.
    // `settle` reads the error to tell that apart from a failed transport.
    onSettled: (_data, error) => settle('scale', error),
  });

  const removeLevel = useMutation({
    mutationFn: (id: string) => {
      setDeletingLevel(id);
      return deleteInvolvementLevel(id);
    },
    onSettled: (_data, error) => {
      setDeletingLevel(null);
      return settle('scale', error);
    },
  });

  const saveTones = useMutation({
    mutationFn: (rows: readonly ToneRow[]) => savePersonaTones(toneBlockOf(rows)),
    onSettled: (_data, error) => settle('tones', error),
  });

  const removeTone = useMutation({
    mutationFn: (id: string) => {
      setDeletingTone(id);
      return deletePersonaTone(id);
    },
    onSettled: (_data, error) => {
      setDeletingTone(null);
      return settle('tones', error);
    },
  });

  // A 401 is the door reopening, not an error of this screen (T6).
  const levelsRejected = useRejectOn401(levels.error);
  const tonesRejected = useRejectOn401(tones.error);

  if (levels.isPending || tones.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner label="Chargement des réglages de personas" />
      </div>
    );
  }

  /*
    The page becomes the error only when there is NOTHING to show - which is
    `isLoadingError` under another name, and it is what narrows the two `data`
    below at the same time.

    On `isError` alone it also became the error, and that unmounted both
    sections - drafts included - the first time a BACKGROUND re-read failed,
    although the data was right there and still displayed a second earlier.
    Every write ends in a re-read, so a flaky network turned one failed save
    into a page that had thrown away work nobody could get back. A failed
    re-read is now a line above the sections, the way `criteres.index` does it.
  */
  if (levels.data === undefined || tones.data === undefined) {
    return (
      <p role="alert" className="text-rouge-deep text-small">
        Les réglages de personas n’ont pas pu être lus. Rechargez l’écran.
      </p>
    );
  }

  // A 401 is already handled above, and its own sentence would be a lie: the
  // door is reopening, nothing is stale.
  const staleRead = (levels.isError && !levelsRejected) || (tones.isError && !tonesRejected);

  return (
    <div className="space-y-10 pb-16">
      <header className="space-y-1">
        <h2 className="font-bold text-bleu-nuit text-h2">Personas</h2>
        <p className="max-w-3xl text-gray-700 text-small">
          Les deux listes de référence que les personas recopient : l’échelle d’implication que
          parcourt leur slider, et la bibliothèque de tons que leur éditeur propose en nuage de
          tags. Ni l’une ni l’autre n’est versionnée : elles se gèrent ici, et nulle part ailleurs.
        </p>
      </header>

      {staleRead ? (
        <p
          role="alert"
          data-testid="reglages-relecture-echouee"
          className="text-rouge-deep text-small"
        >
          La relecture des réglages a échoué. Ce qui est affiché date de la dernière lecture
          réussie.
        </p>
      ) : null}

      <InvolvementScale
        key={`echelle-${scaleGeneration}`}
        levels={levels.data.levels}
        saving={saveScale.isPending}
        deletingId={deletingLevel}
        /*
          One sentence PER GESTURE, and not one for the pair: a failed DELETE
          worded « l'échelle n'a pas pu être enregistrée » sends an operator
          looking for a save they never pressed.
        */
        error={
          refusalOrFailure(saveScale.error, 'L’échelle n’a pas pu être enregistrée. Réessayez.') ??
          refusalOrFailure(removeLevel.error, 'Le cran n’a pas pu être supprimé. Réessayez.')
        }
        onSave={(rows) => saveScale.mutate(rows)}
        onDelete={(id) => removeLevel.mutate(id)}
      />

      <ToneLibrary
        key={`tons-${toneGeneration}`}
        tones={tones.data.tones}
        saving={saveTones.isPending}
        deletingId={deletingTone}
        error={
          refusalOrFailure(
            saveTones.error,
            'La bibliothèque n’a pas pu être enregistrée. Réessayez.',
          ) ?? refusalOrFailure(removeTone.error, 'Le ton n’a pas pu être supprimé. Réessayez.')
        }
        onSave={(rows) => saveTones.mutate(rows)}
        onDelete={(id) => removeTone.mutate(id)}
      />
    </div>
  );
}

/**
 * The typed refusal if there is one, the generic sentence otherwise, `null`
 * when nothing failed.
 *
 * The two must never both appear: a refusal already says what it is, and the
 * generic line is for a failure that carried no code at all.
 */
function refusalOrFailure(error: unknown, fallback: string): string | null {
  if (error === null || error === undefined) return null;
  return personaReferenceMessage(error) ?? fallback;
}
